import React, { useMemo } from 'react'
import classnames from 'classnames'
import SaberCard from '../../components/SaberCard'
import SaberTable from '../../components/SaberTable'
import StatusBadge from '../../components/StatusBadge'
import type { SaberTableColumn } from '../../components/SaberTable'
import { useEscClose } from '../../components/useEscClose'
import { useGetVarianceDetailQuery } from '../../saberApi'
import type { VarianceEffects, VarianceTouRow } from '../../saber.types'
import { date, kwh, percent, rate4, signedInteger, signedMoney } from '../../format'
import { CELL_COLOR, signedKwhDeltaColorClass, varianceColorClass, varianceEffectColorClass } from '../../cellColor'
import { tariffMatchTooltip } from './labels'
import { tableBodyRowClass } from '../../tableRowStyles'

// Variance detail slide-over, ported from the v000 VarianceExplorer drawer.
// Closes on Escape (useEscClose), outside click (backdrop button) and the
// Close button.

const EFFECT_LABELS: Array<{ key: keyof VarianceEffects; label: string }> = [
  { key: 'kwhEffect', label: 'kWh' },
  { key: 'rateEffect', label: 'Rate' },
  { key: 'discountEffect', label: 'Discount' },
  { key: 'allocationEffect', label: 'Allocation' },
  { key: 'bundledEffect', label: 'Bundled' },
  { key: 'interactionEffect', label: 'Interaction' }
]

// Render a billing window as "Feb 1, 2026 – Feb 28, 2026" (em dash, nullish-safe).
const billingPeriod = (start: string | null | undefined, end: string | null | undefined): string =>
  `${date(start)} – ${date(end)}`

export interface VarianceDrawerProps {
  /** saber_45 grain is (invoice, meter): both cuids identify one variance row. */
  selection: { invoiceCuid: string; meterServiceCuid: string }
  onClose: () => void
}

export const VarianceDrawer: React.FC<VarianceDrawerProps> = ({ selection, onClose }) => {
  const { data, isLoading, isError } = useGetVarianceDetailQuery(selection)
  useEscClose(true, onClose)

  const touColumns: Array<SaberTableColumn<VarianceTouRow>> = useMemo(
    () => [
      { key: 'season', header: 'Season', sortable: true, sortValue: (r) => r.season, render: (r) => <StatusBadge status={r.season} palette="season" /> },
      { key: 'tou', header: 'TOU', sortable: true, sortValue: (r) => r.tou, render: (r) => <StatusBadge status={r.tou} palette="tou" /> },
      { key: 'billingKwh', header: 'Billing kWh', align: 'right', sortable: true, sortValue: (r) => r.billingKwh, render: (r) => r.billingKwh.toLocaleString() },
      { key: 'saberKwh', header: 'SABER kWh', align: 'right', sortable: true, sortValue: (r) => r.saberKwh, render: (r) => r.saberKwh.toLocaleString() },
      { key: 'consumedKwh', header: 'Consumed kWh', align: 'right', sortable: true, sortValue: (r) => r.consumedKwh, render: (r) => (r.consumedKwh == null ? '—' : Math.round(r.consumedKwh).toLocaleString()) },
      { key: 'kwhDelta', header: 'Δ kWh', align: 'right', sortable: true, sortValue: (r) => r.kwhDelta, render: (r) => <span className={signedKwhDeltaColorClass(r.kwhDelta)}>{signedInteger(r.kwhDelta)}</span> },
      { key: 'billingRate', header: 'Billing Rate', align: 'right', sortable: true, sortValue: (r) => r.billingRate, render: (r) => rate4(r.billingRate) },
      { key: 'saberRate', header: 'SABER Rate', align: 'right', sortable: true, sortValue: (r) => r.saberRate, render: (r) => rate4(r.saberRate) },
      { key: 'chargeDelta', header: 'Δ Charge', align: 'right', sortable: true, sortValue: (r) => r.chargeDelta, render: (r) => <span className={varianceColorClass(r.chargeDelta)}>{signedMoney(r.chargeDelta)}</span> },
      {
        key: 'tariff',
        header: 'Tariff',
        align: 'center',
        render: (r) => <StatusBadge status={r.tariffMatch ? 'OK' : 'WARN'} title={tariffMatchTooltip(r.tariffMatch)} />
      }
    ],
    []
  )

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Variance detail">
      <button type="button" aria-label="Close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="h-full w-full max-w-7xl overflow-y-auto bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Variance Detail</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-4">
          {isError ? (
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">Failed to load variance detail.</p>
          ) : isLoading || data == null ? (
            <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</p>
          ) : (
            <>
              <SaberCard title="Invoice">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Tenant</dt>
                    <dd>
                      <a
                        href={`/tenant/tenants/${encodeURIComponent(data.tenantCuid)}`}
                        className="font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        {data.tenantName}
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Invoice #</dt>
                    <dd className="text-slate-900 dark:text-slate-100">{data.invoiceNumber ?? '—'}</dd>
                    {/* Cuid kept out of the tables, surfaced here as reference data. */}
                    <dt className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Invoice Cuid
                    </dt>
                    <dd className="break-all font-mono text-xs text-slate-600 dark:text-slate-300">{data.invoiceCuid}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Meter</dt>
                    <dd className="text-slate-900 dark:text-slate-100">{data.meterNumber}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Billing Period</dt>
                    <dd className="text-slate-900 dark:text-slate-100">{billingPeriod(data.billingStartDate, data.billingEndDate)}</dd>
                  </div>
                </dl>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  {data.invoiceNumber != null && data.invoiceNumber !== '' && (
                    <a
                      href={`/collections/invoices?invoiceNumber=${encodeURIComponent(data.invoiceNumber)}`}
                      className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                      View OneBill invoice →
                    </a>
                  )}
                  {/* All of this tenant's invoices in OneBill, filtered by tenant cuid. */}
                  <a
                    href={`/collections/invoices?tenantId=${encodeURIComponent(data.tenantCuid)}`}
                    className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    All invoices for this tenant →
                  </a>
                </div>
              </SaberCard>

              <SaberCard title="DuPont Decomposition">
                {data.capApplied && (
                  <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-300">
                    <span className="font-semibold">Consumption cap applied (WEB-6474):</span> this meter is
                    over-allocated, so the SABER basis below is capped at metered consumption —{' '}
                    {kwh(data.saberConsumedKwh)} consumed vs {kwh(data.actualAllocatedKwh)} billed
                    {data.overallocationKwh != null && <> ({kwh(data.overallocationKwh)} over consumption)</>}.
                    The TOU comparison compares the <span className="font-semibold">uncapped</span> allocation,
                    so its deltas can legitimately differ from the total variance.
                  </div>
                )}
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {EFFECT_LABELS.map(({ key, label }, index) => (
                      <tr key={key} className={tableBodyRowClass(index)}>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{label} effect</td>
                        <td
                          className={classnames(
                            'px-3 py-2 text-right font-medium',
                            varianceEffectColorClass(data.effects[key])
                          )}
                        >
                          {signedMoney(data.effects[key])}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-300 font-semibold dark:border-slate-600">
                      <td className={classnames('px-3 py-2', CELL_COLOR.primary)}>Total variance</td>
                      <td className={classnames('px-3 py-2 text-right font-semibold', varianceColorClass(data.totalVariance))}>
                        {signedMoney(data.totalVariance)} ({percent(data.variancePct)})
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Allocation delta {percent(data.allocationPctDeltaPp)} pp · {data.isOverallocated ? 'over-allocated' : 'within allocation'}
                </p>
              </SaberCard>

              <SaberCard title="TOU Comparison (Billing vs SABER)">
                <SaberTable
                  columns={touColumns}
                  rows={data.touRows}
                  rowKey={(r) => `${r.season}::${r.tou}`}
                  emptyMessage="No TOU rows."
                />
              </SaberCard>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default VarianceDrawer
